import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useReadResource } from '@/hooks/useReadResource';
import ReadNotice from '@/components/feature/ReadNotice';
import { ApiError } from '@/services/appApi';
import { forexApi, toUiAgentId } from '@/services/forexApi';
import { authorizationLabel, readinessLabel } from '@/lib/followPresentation';

export default function RiskPreferencePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { accessToken } = useAuth();
  const [selected, setSelected] = useState(params.get('relationId') || '');
  const identity = accessToken || '';
  const relations = useReadResource(identity, useCallback(() => forexApi.listFollowRelations(accessToken), [accessToken]), !!accessToken);
  const accounts = useReadResource(identity, useCallback(() => forexApi.listFollowerAccounts(accessToken), [accessToken]), !!accessToken);
  const agents = useReadResource(identity, useCallback(() => forexApi.listPlatformAgents(accessToken), [accessToken]), !!accessToken);
  const relation = relations.data?.find(item => item.id === selected) || (!selected ? relations.data?.[0] : undefined);
  const relationId = relation?.id || '';
  const risk = useReadResource(`${identity}:${relationId}`, useCallback(() => forexApi.getSubscriptionAuthorization(relationId, accessToken), [relationId, accessToken]), !!accessToken && !!relationId);
  const value = risk.data;
  const strategy = value?.reviewed_strategy;
  const effective = value?.account_effective_risk;
  const accountLabel = (id: string, fallback?: string) => {
    const label = accounts.data?.find(item => item.id === id)?.account_no || fallback;
    return label && label !== id ? label : '账户资料暂不可用';
  };
  const agentLabel = (id: string | number) => agents.data?.find(item => String(item.agent_id) === String(id))?.name || '策略名称暂不可用';
  const errorStatus = risk.error instanceof ApiError ? risk.error.status : 0;
  const money = (amount: string | undefined, currency: string | undefined) => amount == null || !currency ? '—' : `${amount} ${currency}`;
  const unit = (amount: string | number | undefined, suffix: string) => amount == null ? '—' : `${amount} ${suffix}`;
  return <div className="min-h-screen bg-gray-50 pb-28">
    <div className="bg-white border-b border-gray-100 px-5 pb-3" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 48px)' }}>
      <div className="flex items-center gap-3">
        <button aria-label="返回" onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100">
          <i className="ri-arrow-left-s-line text-gray-600 text-lg" />
        </button>
        <h1 className="text-gray-900 font-bold text-base">策略与账户风险说明</h1>
      </div>
    </div>
    <div className="px-5 pt-4 space-y-3">
      <p className="text-xs text-gray-500">查看已审核策略风险和当前账户风险限制。此页仅供查看。</p>
      <ReadNotice {...relations} label="跟随关系" onRetry={relations.refresh} />
      <ReadNotice {...accounts} label="账户资料" onRetry={accounts.refresh} />
      <ReadNotice {...agents} label="策略名称" onRetry={agents.refresh} />
      {!accessToken && <p>请登录后查看风险说明</p>}
      {relations.data?.length === 0 && !relations.error && <p>暂无可查看的跟随风险说明</p>}
      {!!relations.data?.length && <label className="block rounded-2xl bg-white p-4 text-sm text-gray-600">选择跟随关系
        <select aria-label="选择跟随关系" className="mt-2 w-full rounded-xl border border-gray-200 p-3" value={selected || relationId} onChange={event => setSelected(event.target.value)}>
          {selected && !relation && <option value={selected}>该关系不存在或当前不可访问</option>}
          {relations.data.map((item, index) => <option key={item.id} value={item.id}>关系 {index + 1} · {accountLabel(item.account_id, item.account_no)} · {agentLabel(item.agent_id)}</option>)}
        </select>
      </label>}
      {selected && relations.data && !relation && <p>该关系不存在或当前不可访问，请选择已读取关系。</p>}
      <ReadNotice {...risk} label={errorStatus === 403 ? '无权查看此关系' : errorStatus === 404 ? '关系不存在或当前不可访问' : errorStatus === 401 ? '登录已失效' : '风险说明'} onRetry={risk.refresh} />
      {value && relation && <>
        <section className="rounded-2xl bg-white p-4 text-sm space-y-2">
          <h2 className="font-semibold">已审核策略风险</h2>
          <p>账户：{accountLabel(relation.account_id, relation.account_no)}</p>
          <p>策略：{agentLabel(relation.agent_id)} · 版本 {strategy?.strategy_ref?.version ?? '—'}</p>
          {!strategy && <p>策略风险暂不可用</p>}
          <Rows values={[
            ['固定手数', unit(strategy?.risk_summary?.fixed_lot, 'lot')],
            ['单笔最大手数', unit(strategy?.risk_summary?.max_lot_per_order, 'lot')],
            ['最大总手数', unit(strategy?.risk_summary?.max_total_lots, 'lot')],
            ['最大持仓数', unit(strategy?.risk_summary?.max_open_positions, '笔')],
            ['最大日亏损', money(strategy?.risk_summary?.max_daily_loss, strategy?.risk_summary?.currency)],
            ['最低可用保证金', money(strategy?.risk_summary?.min_free_margin, strategy?.risk_summary?.currency)],
          ]} />
        </section>
        <section className="rounded-2xl bg-white p-4 text-sm space-y-2">
          <h2 className="font-semibold">账户有效风险</h2>
          {effective?.status === 'available' && effective.limits ? <Rows values={[
            ['最大总手数', unit(effective.limits.max_total_lots, 'lot')],
            ['最大持仓数', unit(effective.limits.max_open_positions, '笔')],
            ['最大日亏损', money(effective.limits.max_daily_loss, effective.currency)],
            ['最低可用保证金', money(effective.limits.min_free_margin, effective.currency)],
          ]} /> : <p>账户有效风险暂不可用，不能以策略风险代替。</p>}
        </section>
        <section className="rounded-2xl bg-white p-4 text-sm space-y-2">
          <h2 className="font-semibold">当前授权与新开仓状态</h2>
          <p>{authorizationLabel(value)}</p><p>{readinessLabel(value)}</p>
          <p>新开仓授权：{value.effective_for_new_opens === true ? '已生效，实际执行仍以账户状态及风险检查为准' : '未生效'}</p>
          {value.readiness === 'needs_reauthorization' && !risk.error && !relations.error && <button className="text-indigo-600" onClick={() => navigate(`/copy-config?agentId=${encodeURIComponent(toUiAgentId(String(relation.agent_id)))}${value.validation_binding_id ? '&validation=1' : ''}`)}>前往查看并重新授权</button>}
        </section>
      </>}
      <button className="text-sm text-indigo-600" onClick={() => navigate('/agent?tab=my-copy')}>返回我的跟随</button>
    </div>
  </div>;
}

function Rows({ values }: { values: string[][] }) {
  return <dl className="space-y-2">{values.map(([label, value]) => <div className="flex justify-between gap-3" key={label}><dt className="text-gray-500">{label}</dt><dd>{value}</dd></div>)}</dl>;
}
